/**
 * ContractDiscountDialog — the one Contract Discount dialog, for every place
 * that edits a portfolio's discount (Manage Portfolio's Portfolio details,
 * the Cost Advice page).
 *
 * Why shared: the design (2026-10-21) splits the discount into three terms
 * (On Demand, 1 Year RI, 3 Year RI) with Reset / Cancel / Apply. Two copies of
 * that would drift, as the two single-field dialogs before it already had
 * (different decimals rules, different Enter handling).
 *
 * It edits a draft and hands it over only on Apply, as the strings the fields
 * hold ({ onDemand, oneYearRi, threeYearRi }, '' = blank). Reset empties all
 * three (user order 2026-10-26: nothing is left in the fields — it replaced
 * "back to the initial values"); Cancel (or ✕ / Escape) closes with nothing
 * changed. Apply is off — and Enter does nothing — until at least one term
 * has a value (user order 2026-10-26).
 *
 * "Apply same contract discount to all pricing models" (design, 2026-10-23):
 * while ticked, the three terms move together — typing in any one sets all
 * three, and ticking it copies the first value already entered into the rest.
 * Unticking leaves the values as they are and frees each field again. It
 * always starts unticked, and Reset unticks it (user order 2026-10-23). It
 * is a UI helper only: Apply still hands over the three values, nothing more.
 *
 * The 0-99.90 rule, with at most two decimals, is enforced while typing
 * (acceptsTermInput): a keystroke that would go past 99.90 or add a third
 * decimal is refused, as the minus and exponent keys already are.
 */
import { useEffect, useId, useRef, useState } from 'react';

import { NumberField } from '@components/common/NumberField';
import { ClearIcon, RefreshIcon } from '@components/common/icons';
import { Button, Modal } from '@components/ui';

import {
  DISCOUNT_TERM_HINT,
  DISCOUNT_TERM_MAX,
  DISCOUNT_TERM_MIN,
  DISCOUNT_TERM_PLACEHOLDER,
  DISCOUNT_TERM_STEP,
  DISCOUNT_TERMS,
  acceptsTermInput,
  contractDiscountErrors,
  emptyContractDiscount,
  fillAllTerms,
  hasContractDiscountValue,
  toContractDiscountDraft,
} from './contractDiscountRules';

export const SAME_DISCOUNT_LABEL = 'Apply same contract discount to all pricing models';

import styles from './ContractDiscountDialog.module.css';

export const CONTRACT_DISCOUNT_TITLE = 'Contract Discount';
export const CONTRACT_DISCOUNT_SUBTITLE = 'Applied to both current and recommended catalog prices.';

/**
 * The header: title with the one-line description under it, both in the
 * header band (Figma, 2026-10-23), not in the body.
 *
 * The library Modal takes a node as `title`, but then names the dialog just
 * "Dialog" for screen readers (it only uses a STRING title as aria-label). So
 * the dialog's name and description are set back here, on mount.
 */
function DialogHeading() {
  const ref = useRef(null);
  const descriptionId = useId();
  useEffect(() => {
    const dialog = ref.current?.closest('[role="dialog"]');
    if (!dialog) return;
    dialog.setAttribute('aria-label', CONTRACT_DISCOUNT_TITLE);
    dialog.setAttribute('aria-describedby', descriptionId);
  }, [descriptionId]);

  return (
    <div ref={ref} className={styles.heading}>
      <h4>{CONTRACT_DISCOUNT_TITLE}</h4>
      <p id={descriptionId} className={styles.subtitle}>
        {CONTRACT_DISCOUNT_SUBTITLE}
      </p>
    </div>
  );
}

/**
 * @param {object}   props
 * @param {object}   [props.value]  { onDemand, oneYearRi, threeYearRi } to start from
 * @param {(value: {onDemand: string, oneYearRi: string, threeYearRi: string}) => void} props.onApply
 * @param {() => void} props.onClose
 * @param {string}   [props.idPrefix]  keeps field ids unique if two pages mount it
 */
export function ContractDiscountDialog({
  value,
  onApply,
  onClose,
  idPrefix = 'contract-discount',
}) {
  const [draft, setDraft] = useState(() => toContractDiscountDraft(value));
  // Always opens unticked (user order 2026-10-23): the user ticks it.
  const [same, setSame] = useState(false);
  // Per term: has a "." been typed in its field (see the key handler below)?
  const dotted = useRef({});
  const sameId = `${idPrefix}-same-${useId()}`;
  const errors = contractDiscountErrors(draft);
  // Apply needs at least one value, and no term in error (user order
  // 2026-10-26); otherwise it is off and does nothing (button or Enter).
  const invalid = Object.keys(errors).length > 0 || !hasContractDiscountValue(draft);

  const set = (key, next) => {
    // Past 99 (or not a whole number): refused, the field keeps its value.
    if (!acceptsTermInput(next)) return;
    // Blank, or a whole number other than the "0" a "0." is reported as (whose
    // "." was just typed): the field has no "." any more.
    if (next === '' || (next !== '0' && !String(next).includes('.'))) dotted.current[key] = false;
    setDraft((d) => (same ? fillAllTerms(d, next) : { ...d, [key]: next }));
  };
  const toggleSame = (checked) => {
    setSame(checked);
    if (checked) setDraft((d) => fillAllTerms(d));
  };
  const reset = () => {
    setDraft(emptyContractDiscount());
    setSame(false);
    dotted.current = {};
  };
  const apply = () => {
    if (!invalid) onApply?.(draft);
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={<DialogHeading />}
      footer={
        <div className={styles.actions}>
          <Button
            variant="secondary-solid"
            className={styles.button}
            startIcon={<RefreshIcon />}
            onClick={reset}
          >
            Reset
          </Button>
          <span className={styles.spacer} />
          <Button
            variant="secondary-solid"
            className={styles.button}
            startIcon={<ClearIcon />}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button variant="primary" className={styles.button} disabled={invalid} onClick={apply}>
            Apply
          </Button>
        </div>
      }
    >
      <label htmlFor={sameId} className={styles.same}>
        <input
          id={sameId}
          type="checkbox"
          className={styles.checkbox}
          checked={same}
          onChange={(e) => toggleSame(e.target.checked)}
        />
        {SAME_DISCOUNT_LABEL}
      </label>
      <div className={styles.fields}>
        {DISCOUNT_TERMS.map((term) => (
          <NumberField
            key={term.key}
            id={`${idPrefix}-${term.key}`}
            label={term.label}
            value={draft[term.key]}
            onChange={(e) => set(term.key, e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') apply();
              if (e.key === '.') dotted.current[term.key] = true;
              // Backspace on a field reported as "0" removes its last character:
              // the "." if there was one ("0." -> "0"), else the 0 itself. Either
              // way no "." is left. From "0.1" it removes the 1, the "." stays.
              else if (e.key === 'Backspace' && draft[term.key] === '0') {
                dotted.current[term.key] = false;
              }
              // A leading 0 allows only a "." after it. Stopped at the key, not
              // only in `set`: a refused change puts the old value back, and the
              // caret can land BEFORE the 0, so the next digit would make "10".
              // Not once a "." is typed: "0.5" is fine, and the field cannot say
              // "0" from "0." (a number input may report both as "0").
              if (/^\d$/.test(e.key) && draft[term.key] === '0' && !dotted.current[term.key]) {
                e.preventDefault();
              }
            }}
            min={DISCOUNT_TERM_MIN}
            max={DISCOUNT_TERM_MAX}
            step={DISCOUNT_TERM_STEP}
            placeholder={DISCOUNT_TERM_PLACEHOLDER}
            hint={DISCOUNT_TERM_HINT}
            error={errors[term.key]}
          />
        ))}
      </div>
    </Modal>
  );
}

export default ContractDiscountDialog;
